import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const timelineData = {
  medical: [
    { year: 'Age 18', label: 'Undergrad Pre-Med', detail: '4 years of biology, chemistry, physics, and MCAT prep', icon: 'ri-book-open-line' },
    { year: 'Age 22', label: 'Medical School', detail: '4 years of anatomy, pharmacology, clinical rotations', icon: 'ri-hospital-line' },
    { year: 'Age 26', label: 'Residency', detail: '3–7 years of intensive clinical training, 80-hour weeks', icon: 'ri-stethoscope-line' },
    { year: 'Age 30–33', label: 'Fellowship (Optional)', detail: '1–3 years of subspecialty training', icon: 'ri-award-line' },
    { year: 'Age 33+', label: 'Licensed Physician', detail: '15+ years of training. You are now a world-class healer.', icon: 'ri-heart-pulse-line', highlight: true },
  ],
  mba: [
    { year: 'Age 18', label: 'Undergrad Business', detail: '4 years of accounting, finance, marketing, economics', icon: 'ri-building-line' },
    { year: 'Age 22', label: 'MBA Program', detail: '2 years of operations, HR, strategy, and management', icon: 'ri-briefcase-line' },
    { year: 'Age 24', label: 'Business Experience', detail: 'Years of real-world operations, compliance, and finance', icon: 'ri-line-chart-line' },
    { year: 'Age 28', label: 'Healthcare Admin Expert', detail: 'Specialized in medical practice operations and revenue', icon: 'ri-settings-line' },
    { year: 'Age 30+', label: 'MSO Professional', detail: 'Expert in running the business side of healthcare.', icon: 'ri-trophy-line', highlight: true },
  ],
};

const failurePath = [
  { icon: 'ri-time-line', title: 'Time Stolen from Patients', desc: 'Spending 3–4 hours/day on billing, HR, and admin instead of seeing patients' },
  { icon: 'ri-money-dollar-circle-line', title: 'Revenue Leakage', desc: 'Up to 30% of revenue lost to billing errors, denials, and poor coding' },
  { icon: 'ri-emotion-unhappy-line', title: 'Physician Burnout', desc: '63% of physicians report burnout — administrative burden is the #1 cause' },
  { icon: 'ri-error-warning-line', title: 'Compliance Risks', desc: 'HIPAA violations, credentialing lapses, and regulatory fines from oversight gaps' },
  { icon: 'ri-team-line', title: 'Staff Turnover', desc: 'Without proper HR, practices lose 40% of staff annually — costing $15K+ per hire' },
  { icon: 'ri-close-circle-line', title: 'Practice Closure', desc: '50% of independent practices close within 5 years due to business mismanagement' },
];

const successPath = [
  { icon: 'ri-stethoscope-line', title: 'Focus on Medicine', desc: 'Spend 100% of your energy on patient care, diagnosis, and clinical excellence' },
  { icon: 'ri-line-chart-line', title: 'Revenue Optimized', desc: 'Expert billing and coding increases collections by 25–35% on average' },
  { icon: 'ri-heart-line', title: 'Zero Burnout', desc: 'Physicians who delegate admin report 40% higher job satisfaction' },
  { icon: 'ri-shield-check-line', title: 'Full Compliance', desc: 'Dedicated compliance team ensures zero regulatory gaps or HIPAA risks' },
  { icon: 'ri-group-line', title: 'Stable Dream Team', desc: 'Professional HR retains top staff and builds a high-performing practice culture' },
  { icon: 'ri-building-2-line', title: 'Practice Thrives', desc: 'MSO-managed practices grow 3x faster and achieve long-term sustainability' },
];

const stats = [
  { value: '15+', label: 'Years of Medical Training', sub: 'vs. 0 years of business training' },
  { value: '63%', label: 'Physician Burnout Rate', sub: 'Admin burden is the #1 cause' },
  { value: '30%', label: 'Revenue Lost to Billing Errors', sub: 'Without expert RCM management' },
  { value: '3x', label: 'Faster Practice Growth', sub: 'With professional MSO support' },
];

export default function InfographicPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.15 }
    );
    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });
    return () => observer.disconnect();
  }, []);

  const setRef = (id: string) => (el: HTMLDivElement | null) => {
    sectionRefs.current[id] = el;
  };

  const handleGetStarted = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('contact');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-gradient-to-b from-[#0A1628] to-[#1B4B7F]'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3 cursor-pointer">
              <img
                src="https://static.readdy.ai/image/50f4002bde5ecf02750e3107d0496ef3/6f24c25eed393f63407e0e21645fee7d.png"
                alt="MedxMSO Logo"
                className="h-12 w-auto"
              />
              <span className={`text-xl font-black tracking-tight whitespace-nowrap ${scrolled ? 'text-[#1B4B7F]' : 'text-white'}`}>MedxMSO</span>
            </Link>
            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#1B4B7F]' : 'text-white hover:text-gray-200'}`}>Home</Link>
              <Link to="/services" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#1B4B7F]' : 'text-white hover:text-gray-200'}`}>Services</Link>
              <Link to="/pricing" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#1B4B7F]' : 'text-white hover:text-gray-200'}`}>Models</Link>
              <Link to="/mso-pricing" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#1B4B7F]' : 'text-white hover:text-gray-200'}`}>Vendor Pricing</Link>
              <Link to="/partnership" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-[#059669] hover:text-[#047857]' : 'text-[#6EE7B7] hover:text-white'}`}>Partnership</Link>
              <Link to="/why-mso" className={`text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-[#1B4B7F]' : 'text-white'}`}>Why MSO?</Link>
              <Link to="/audit" className={`text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-[#BE185D] hover:text-[#9D174D]' : 'text-[#F9A8D4] hover:text-white'}`}>Request Audit</Link>
              <button onClick={handleGetStarted} className="bg-[#1B4B7F] text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-[#153a63] transition-colors whitespace-nowrap cursor-pointer">
                Get Started
              </button>
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 cursor-pointer">
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${scrolled ? 'text-gray-700' : 'text-white'}`}></i>
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t">
            <div className="px-6 py-4 space-y-3">
              <Link to="/" className="block text-sm font-medium text-gray-700 hover:text-[#1B4B7F] cursor-pointer">Home</Link>
              <Link to="/services" className="block text-sm font-medium text-gray-700 hover:text-[#1B4B7F] cursor-pointer">Services</Link>
              <Link to="/pricing" className="block text-sm font-medium text-gray-700 hover:text-[#1B4B7F] cursor-pointer">Models</Link>
              <Link to="/mso-pricing" className="block text-sm font-medium text-gray-700 hover:text-[#1B4B7F] cursor-pointer">Vendor Pricing</Link>
              <Link to="/partnership" className="block text-sm font-semibold text-[#059669] cursor-pointer">Partnership</Link>
              <Link to="/why-mso" className="block text-sm font-semibold text-[#1B4B7F] cursor-pointer">Why MSO?</Link>
              <Link to="/audit" className="block text-sm font-semibold text-[#BE185D] cursor-pointer">Request Audit</Link>
              <button onClick={handleGetStarted} className="block w-full bg-[#1B4B7F] text-white px-6 py-2.5 rounded-full text-sm font-medium text-center cursor-pointer">Get Started</button>
            </div>
          </div>
        )}
      </nav>

      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/8033e64f3066cba64dd389afe2d27b11.jpg"
            alt="Medicine vs Business"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/90 via-[#0A1628]/70 to-[#0A1628]/50"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-40 w-full">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[3px] text-[#0D9488] mb-6 font-semibold">THE DOCTOR'S DILEMMA</p>
            <h1 className="text-6xl lg:text-8xl font-black text-white leading-none mb-6">
              You Trained<br />
              to <span className="text-[#0D9488]">Heal.</span><br />
              Not to<br />
              <span className="text-[#F59E0B]">Manage.</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-10 max-w-xl">
              15+ years of medical training made you an exceptional physician. Zero of those years prepared you to run a business. So why are you doing both?
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#two-paths" className="bg-[#0D9488] text-white px-8 py-4 rounded-full text-sm font-semibold hover:bg-[#0b7a70] transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer">
                See the Two Paths
                <i className="ri-arrow-down-line"></i>
              </a>
              <Link to="/#contact" className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-full text-sm font-semibold hover:bg-white hover:text-[#0A1628] transition-all whitespace-nowrap cursor-pointer">
                Talk to an Expert
              </Link>
            </div>
          </div>
        </div>
        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/60 animate-bounce">
          <span className="text-xs tracking-widest uppercase">Scroll</span>
          <i className="ri-arrow-down-line text-xl"></i>
        </div>
      </section>

      {/* STATS BANNER */}
      <section className="bg-[#0A1628] py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-5xl font-black text-[#0D9488] mb-2">{stat.value}</div>
                <div className="text-white font-semibold text-sm mb-1">{stat.label}</div>
                <div className="text-gray-500 text-xs">{stat.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TWO TRACKS TIMELINE */}
      <section id="two-paths" className="py-28 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <p className="text-xs uppercase tracking-[3px] text-[#0D9488] mb-4 font-semibold">PARALLEL JOURNEYS</p>
            <h2 className="text-5xl lg:text-6xl font-black text-gray-900 leading-tight mb-6">
              Two Tracks. Two Experts.<br />
              <span className="text-[#1B4B7F]">One Winning Team.</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A physician and an MSO professional each spend 15+ years mastering their craft. The smartest practices combine both.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Medical Track */}
            <div
              id="medical-track"
              ref={setRef('medical-track')}
              className={`transition-all duration-700 ${visibleSections.has('medical-track') ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}
            >
              <div className="bg-gradient-to-br from-[#1B4B7F] to-[#153a63] rounded-3xl p-10 h-full">
                <div className="flex items-center gap-4 mb-10">
                  <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                    <i className="ri-stethoscope-line text-white text-3xl"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-white">Medical School Track</h3>
                    <p className="text-blue-200 text-sm">The Path to Healing</p>
                  </div>
                </div>
                <div className="relative">
                  <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-white/20"></div>
                  <div className="space-y-8">
                    {timelineData.medical.map((item, i) => (
                      <div key={i} className="flex gap-6 relative">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${item.highlight ? 'bg-[#0D9488]' : 'bg-white/20'}`}>
                          <i className={`${item.icon} text-white text-lg`}></i>
                        </div>
                        <div className={`flex-1 pb-2 ${item.highlight ? 'bg-white/10 rounded-2xl p-4 -mt-1' : ''}`}>
                          <div className="text-xs text-blue-300 font-semibold mb-1">{item.year}</div>
                          <div className={`font-bold mb-1 ${item.highlight ? 'text-[#0D9488] text-lg' : 'text-white'}`}>{item.label}</div>
                          <div className="text-blue-200 text-sm leading-relaxed">{item.detail}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-10 bg-white/10 rounded-2xl p-6">
                  <div className="text-white font-bold text-lg mb-2">Total Training: 15+ Years</div>
                  <div className="text-blue-200 text-sm">Mastery in: Diagnosis · Treatment · Patient Care · Clinical Excellence</div>
                  <div className="mt-3 text-yellow-300 text-sm font-semibold">⚠ Business training: 0 hours</div>
                </div>
              </div>
            </div>

            {/* MBA Track */}
            <div
              id="mba-track"
              ref={setRef('mba-track')}
              className={`transition-all duration-700 delay-200 ${visibleSections.has('mba-track') ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}
            >
              <div className="bg-gradient-to-br from-[#0D9488] to-[#0b7a70] rounded-3xl p-10 h-full">
                <div className="flex items-center gap-4 mb-10">
                  <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                    <i className="ri-briefcase-line text-white text-3xl"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-white">MBA / MSO Track</h3>
                    <p className="text-teal-100 text-sm">The Path to Business Mastery</p>
                  </div>
                </div>
                <div className="relative">
                  <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-white/20"></div>
                  <div className="space-y-8">
                    {timelineData.mba.map((item, i) => (
                      <div key={i} className="flex gap-6 relative">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${item.highlight ? 'bg-[#1B4B7F]' : 'bg-white/20'}`}>
                          <i className={`${item.icon} text-white text-lg`}></i>
                        </div>
                        <div className={`flex-1 pb-2 ${item.highlight ? 'bg-white/10 rounded-2xl p-4 -mt-1' : ''}`}>
                          <div className="text-xs text-teal-200 font-semibold mb-1">{item.year}</div>
                          <div className={`font-bold mb-1 ${item.highlight ? 'text-[#1B4B7F] text-lg' : 'text-white'}`}>{item.label}</div>
                          <div className="text-teal-100 text-sm leading-relaxed">{item.detail}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-10 bg-white/10 rounded-2xl p-6">
                  <div className="text-white font-bold text-lg mb-2">Total Training: 12+ Years</div>
                  <div className="text-teal-100 text-sm">Mastery in: Operations · HR · Finance · Compliance · Revenue Cycle</div>
                  <div className="mt-3 text-yellow-300 text-sm font-semibold">⚠ Clinical training: 0 hours</div>
                </div>
              </div>
            </div>
          </div>

          {/* Connector */}
          <div
            id="connector"
            ref={setRef('connector')}
            className={`mt-16 transition-all duration-700 ${visibleSections.has('connector') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="bg-gradient-to-r from-[#1B4B7F] via-[#0A1628] to-[#0D9488] rounded-3xl p-10 text-center">
              <div className="text-4xl font-black text-white mb-4">
                Doctor + MSO = <span className="text-[#F59E0B]">Unstoppable Practice</span>
              </div>
              <p className="text-gray-300 text-lg max-w-2xl mx-auto">
                When a world-class physician partners with a world-class MSO, patients get better care and the practice thrives. That's the MedxMSO model.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FORK IN THE ROAD */}
      <section className="py-28 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <p className="text-xs uppercase tracking-[3px] text-[#0D9488] mb-4 font-semibold">THE CRITICAL DECISION</p>
            <h2 className="text-5xl lg:text-6xl font-black text-gray-900 leading-tight mb-6">
              The Fork in the Road
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Every physician faces this moment. The path you choose determines whether your practice struggles or soars.
            </p>
          </div>

          {/* Fork Visual */}
          <div className="relative mb-20">
            <div className="flex flex-col items-center">
              {/* Top - The Doctor */}
              <div className="bg-[#1B4B7F] text-white rounded-2xl px-10 py-6 text-center mb-0 relative z-10 shadow-2xl">
                <div className="text-2xl font-black mb-1">You — The Physician</div>
                <div className="text-blue-200 text-sm">15+ years of medical training. Ready to practice.</div>
              </div>
              {/* Arrow down */}
              <div className="w-0.5 h-12 bg-gray-300"></div>
              {/* Decision Diamond */}
              <div className="w-40 h-40 bg-[#F59E0B] rotate-45 flex items-center justify-center shadow-2xl relative z-10">
                <div className="-rotate-45 text-center">
                  <div className="text-white font-black text-lg leading-tight">Choose<br />Your<br />Path</div>
                </div>
              </div>
              {/* Two paths */}
              <div className="w-full flex justify-between items-start mt-0 relative">
                {/* Left line */}
                <div className="flex-1 flex flex-col items-center">
                  <div className="w-full h-0.5 bg-red-300 mt-0"></div>
                </div>
                <div className="w-40"></div>
                {/* Right line */}
                <div className="flex-1 flex flex-col items-center">
                  <div className="w-full h-0.5 bg-green-400 mt-0"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Two Paths Side by Side */}
          <div className="grid lg:grid-cols-2 gap-10">
            {/* FAILURE PATH */}
            <div
              id="failure-path"
              ref={setRef('failure-path')}
              className={`transition-all duration-700 ${visibleSections.has('failure-path') ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}
            >
              <div className="bg-gradient-to-b from-red-50 to-red-100/50 border-2 border-red-200 rounded-3xl overflow-hidden">
                <div className="bg-red-600 px-10 py-8 text-center">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-close-circle-line text-white text-4xl"></i>
                  </div>
                  <h3 className="text-3xl font-black text-white mb-2">Path to Failure</h3>
                  <p className="text-red-100 text-sm">"I'll handle the business myself"</p>
                </div>
                <div className="p-10">
                  <div className="space-y-5">
                    {failurePath.map((item, i) => (
                      <div key={i} className="flex items-start gap-4 bg-white rounded-2xl p-5 shadow-sm border border-red-100">
                        <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <i className={`${item.icon} text-red-500 text-xl`}></i>
                        </div>
                        <div>
                          <div className="font-bold text-gray-900 text-sm mb-1">{item.title}</div>
                          <div className="text-gray-600 text-xs leading-relaxed">{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 bg-red-600 rounded-2xl p-6 text-center">
                    <div className="text-white font-black text-xl mb-2">End Result</div>
                    <div className="text-red-100 text-sm">Burnout · Revenue Loss · Practice Closure</div>
                    <div className="text-white text-3xl font-black mt-3">50% Close in 5 Years</div>
                  </div>
                </div>
              </div>
            </div>

            {/* SUCCESS PATH */}
            <div
              id="success-path"
              ref={setRef('success-path')}
              className={`transition-all duration-700 delay-200 ${visibleSections.has('success-path') ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}
            >
              <div className="bg-gradient-to-b from-green-50 to-teal-50 border-2 border-[#0D9488]/30 rounded-3xl overflow-hidden">
                <div className="bg-gradient-to-r from-[#1B4B7F] to-[#0D9488] px-10 py-8 text-center">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-checkbox-circle-line text-white text-4xl"></i>
                  </div>
                  <h3 className="text-3xl font-black text-white mb-2">Path to Success</h3>
                  <p className="text-blue-100 text-sm">"I'll partner with MedxMSO"</p>
                </div>
                <div className="p-10">
                  <div className="space-y-5">
                    {successPath.map((item, i) => (
                      <div key={i} className="flex items-start gap-4 bg-white rounded-2xl p-5 shadow-sm border border-teal-100">
                        <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                          <i className={`${item.icon} text-[#0D9488] text-xl`}></i>
                        </div>
                        <div>
                          <div className="font-bold text-gray-900 text-sm mb-1">{item.title}</div>
                          <div className="text-gray-600 text-xs leading-relaxed">{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 bg-gradient-to-r from-[#1B4B7F] to-[#0D9488] rounded-2xl p-6 text-center">
                    <div className="text-white font-black text-xl mb-2">End Result</div>
                    <div className="text-blue-100 text-sm">Thriving Practice · Happy Patients · Financial Freedom</div>
                    <div className="text-white text-3xl font-black mt-3">3x Faster Growth</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT DOCTORS SHOULD DO */}
      <section className="py-28 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div
            id="doctor-focus"
            ref={setRef('doctor-focus')}
            className={`transition-all duration-700 ${visibleSections.has('doctor-focus') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
          >
            <div className="text-center mb-20">
              <p className="text-xs uppercase tracking-[3px] text-[#0D9488] mb-4 font-semibold">THE SIMPLE TRUTH</p>
              <h2 className="text-5xl lg:text-6xl font-black text-gray-900 leading-tight mb-6">
                Doctors Should Do<br />
                <span className="text-[#1B4B7F]">What Doctors Do Best</span>
              </h2>
            </div>

            <div className="grid lg:grid-cols-3 gap-8 mb-16">
              {/* Doctor's Zone */}
              <div className="lg:col-span-2">
                <div className="bg-gradient-to-br from-[#1B4B7F] to-[#0D9488] rounded-3xl p-10 h-full">
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                      <i className="ri-heart-pulse-line text-white text-3xl"></i>
                    </div>
                    <div>
                      <h3 className="text-2xl font-black text-white">The Doctor's Zone</h3>
                      <p className="text-blue-200 text-sm">Where your 15 years of training pays off</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { icon: 'ri-stethoscope-line', label: 'Diagnose & Treat', desc: 'Apply your clinical expertise to complex cases' },
                      { icon: 'ri-heart-line', label: 'Patient Relationships', desc: 'Build trust and deliver compassionate care' },
                      { icon: 'ri-microscope-line', label: 'Clinical Research', desc: 'Stay current with medical advances' },
                      { icon: 'ri-team-line', label: 'Mentor & Teach', desc: 'Train the next generation of physicians' },
                      { icon: 'ri-mental-health-line', label: 'Complex Cases', desc: 'Focus on the patients who need you most' },
                      { icon: 'ri-award-line', label: 'Clinical Excellence', desc: 'Pursue board certifications and specializations' },
                    ].map((item, i) => (
                      <div key={i} className="bg-white/10 rounded-2xl p-5 flex items-start gap-3">
                        <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                          <i className={`${item.icon} text-white text-lg`}></i>
                        </div>
                        <div>
                          <div className="text-white font-bold text-sm mb-1">{item.label}</div>
                          <div className="text-blue-200 text-xs">{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* MSO's Zone */}
              <div>
                <div className="bg-[#0A1628] rounded-3xl p-10 h-full">
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center">
                      <i className="ri-building-2-line text-[#0D9488] text-3xl"></i>
                    </div>
                    <div>
                      <h3 className="text-2xl font-black text-white">MedxMSO's Zone</h3>
                      <p className="text-gray-400 text-sm">We handle everything else</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {[
                      { icon: 'ri-money-dollar-circle-line', label: 'Billing & Coding' },
                      { icon: 'ri-shield-check-line', label: 'HIPAA Compliance' },
                      { icon: 'ri-group-line', label: 'HR & Staffing' },
                      { icon: 'ri-file-list-3-line', label: 'Credentialing' },
                      { icon: 'ri-computer-line', label: 'IT & EHR Systems' },
                      { icon: 'ri-calculator-line', label: 'Financial Reporting' },
                      { icon: 'ri-customer-service-line', label: 'Payer Negotiations' },
                      { icon: 'ri-bar-chart-line', label: 'Revenue Optimization' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3">
                        <div className="w-8 h-8 bg-[#0D9488]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className={`${item.icon} text-[#0D9488] text-base`}></i>
                        </div>
                        <span className="text-gray-300 text-sm font-medium">{item.label}</span>
                        <i className="ri-check-line text-[#0D9488] ml-auto"></i>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* The Big Quote */}
            <div className="bg-gradient-to-r from-[#1B4B7F] to-[#0D9488] rounded-3xl p-14 text-center relative overflow-hidden">
              <div className="absolute top-6 left-10 text-white/10 text-[120px] font-serif leading-none select-none">"</div>
              <div className="relative z-10">
                <p className="text-3xl lg:text-4xl font-black text-white leading-tight mb-6 max-w-4xl mx-auto">
                  "You didn't spend 15 years in medical school to become a billing specialist, HR manager, and compliance officer. You trained to save lives. Let us handle the rest."
                </p>
                <div className="text-blue-200 text-base font-semibold">— MedxMSO Philosophy</div>
              </div>
              <div className="absolute bottom-6 right-10 text-white/10 text-[120px] font-serif leading-none select-none rotate-180">"</div>
            </div>
          </div>
        </div>
      </section>

      {/* VISUAL COMPARISON: TIME ALLOCATION */}
      <section className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <p className="text-xs uppercase tracking-[3px] text-[#0D9488] mb-4 font-semibold">TIME IS YOUR MOST VALUABLE ASSET</p>
            <h2 className="text-5xl lg:text-6xl font-black text-gray-900 leading-tight mb-6">
              Where Does Your Day Go?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The average physician without MSO support spends nearly half their day on non-clinical tasks.
            </p>
          </div>

          <div
            id="time-comparison"
            ref={setRef('time-comparison')}
            className={`grid lg:grid-cols-2 gap-12 transition-all duration-700 ${visibleSections.has('time-comparison') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
          >
            {/* Without MSO */}
            <div className="bg-red-50 border-2 border-red-200 rounded-3xl p-10">
              <h3 className="text-2xl font-black text-red-700 mb-2">Without MSO Support</h3>
              <p className="text-red-500 text-sm mb-8">A typical 10-hour physician workday</p>
              <div className="space-y-4">
                {[
                  { label: 'Patient Care', pct: 45, color: 'bg-[#1B4B7F]', hours: '4.5 hrs' },
                  { label: 'Administrative Tasks', pct: 25, color: 'bg-red-400', hours: '2.5 hrs' },
                  { label: 'Billing & Coding Issues', pct: 15, color: 'bg-red-500', hours: '1.5 hrs' },
                  { label: 'HR & Staff Issues', pct: 10, color: 'bg-red-600', hours: '1.0 hr' },
                  { label: 'Compliance & Paperwork', pct: 5, color: 'bg-red-700', hours: '0.5 hr' },
                ].map((item, i) => (
                  <div key={i}>
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="text-sm font-semibold text-gray-700">{item.label}</span>
                      <span className="text-sm font-bold text-gray-900">{item.hours}</span>
                    </div>
                    <div className="h-8 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${item.color} rounded-full flex items-center justify-end pr-3 transition-all duration-1000`}
                        style={{ width: visibleSections.has('time-comparison') ? `${item.pct}%` : '0%' }}
                      >
                        <span className="text-white text-xs font-bold">{item.pct}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 bg-red-100 rounded-2xl p-5 text-center">
                <div className="text-red-700 font-black text-2xl">55% of your day</div>
                <div className="text-red-600 text-sm mt-1">wasted on non-clinical tasks</div>
              </div>
            </div>

            {/* With MSO */}
            <div className="bg-gradient-to-b from-teal-50 to-white border-2 border-[#0D9488]/30 rounded-3xl p-10">
              <h3 className="text-2xl font-black text-[#0D9488] mb-2">With MedxMSO Support</h3>
              <p className="text-teal-600 text-sm mb-8">Your optimized 10-hour physician workday</p>
              <div className="space-y-4">
                {[
                  { label: 'Patient Care', pct: 80, color: 'bg-[#1B4B7F]', hours: '8.0 hrs' },
                  { label: 'Clinical Research & Learning', pct: 10, color: 'bg-[#0D9488]', hours: '1.0 hr' },
                  { label: 'Team Leadership', pct: 7, color: 'bg-teal-400', hours: '0.7 hr' },
                  { label: 'MSO Coordination', pct: 3, color: 'bg-teal-300', hours: '0.3 hr' },
                ].map((item, i) => (
                  <div key={i}>
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="text-sm font-semibold text-gray-700">{item.label}</span>
                      <span className="text-sm font-bold text-gray-900">{item.hours}</span>
                    </div>
                    <div className="h-8 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${item.color} rounded-full flex items-center justify-end pr-3 transition-all duration-1000 delay-300`}
                        style={{ width: visibleSections.has('time-comparison') ? `${item.pct}%` : '0%' }}
                      >
                        <span className="text-white text-xs font-bold">{item.pct}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 bg-gradient-to-r from-[#1B4B7F] to-[#0D9488] rounded-2xl p-5 text-center">
                <div className="text-white font-black text-2xl">80% of your day</div>
                <div className="text-blue-100 text-sm mt-1">focused on what you love — medicine</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHICH PATH IS RIGHT FOR YOU — NEW SECTION */}
      <section className="py-28 bg-[#0A1628]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <p className="text-xs uppercase tracking-[3px] text-[#6EE7B7] mb-4 font-semibold">TWO WAYS TO WORK WITH MEDXMSO</p>
            <h2 className="text-5xl lg:text-6xl font-black text-white leading-tight mb-6">
              Which Path Is<br />
              <span className="text-[#6EE7B7]">Right for You?</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Once you decide to stop going it alone, you have two distinct options. Both protect your clinical independence — the difference is how deep you want to go.
            </p>
          </div>

          {/* Decision Quiz */}
          <div className="max-w-4xl mx-auto mb-16">
            <div className="bg-white/5 border border-white/10 rounded-3xl p-10">
              <h3 className="text-xl font-black text-white mb-8 text-center">Ask yourself these questions:</h3>
              <div className="space-y-5">
                {[
                  { q: 'Do you want to keep your practice economics (collections minus fees)?', vendor: true, partner: false },
                  { q: 'Do you want a guaranteed fixed income regardless of collections?', vendor: false, partner: true },
                  { q: 'Do you want to build equity in a growing healthcare organization?', vendor: false, partner: true },
                  { q: 'Do you run a multi-provider group or specialty clinic?', vendor: true, partner: false },
                  { q: 'Are you a single-practice physician looking for a deeper partnership?', vendor: false, partner: true },
                  { q: 'Do you want to pay only for what you use (ops + optional RCM)?', vendor: true, partner: false },
                ].map((item, i) => (
                  <div key={i} className="grid grid-cols-12 gap-4 items-center bg-white/5 rounded-2xl px-6 py-4">
                    <div className="col-span-8 text-gray-300 text-sm leading-relaxed">{item.q}</div>
                    <div className="col-span-2 text-center">
                      {item.vendor ? (
                        <span className="inline-flex items-center gap-1 bg-[#1B4B7F]/40 text-[#93C5FD] px-3 py-1 rounded-full text-xs font-bold">
                          <i className="ri-check-line"></i> Vendor
                        </span>
                      ) : (
                        <span className="text-gray-600 text-xs">—</span>
                      )}
                    </div>
                    <div className="col-span-2 text-center">
                      {item.partner ? (
                        <span className="inline-flex items-center gap-1 bg-[#059669]/30 text-[#6EE7B7] px-3 py-1 rounded-full text-xs font-bold">
                          <i className="ri-check-line"></i> Partner
                        </span>
                      ) : (
                        <span className="text-gray-600 text-xs">—</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Two Track Cards */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Vendor Track */}
            <div className="bg-white/5 border border-[#1B4B7F]/50 rounded-3xl p-10 hover:bg-white/10 transition-all duration-300">
              <div className="inline-flex items-center gap-2 bg-[#1B4B7F]/40 text-[#93C5FD] px-4 py-1.5 rounded-full text-xs font-bold tracking-widest mb-6">
                <i className="ri-briefcase-line"></i>
                TRACK 1 — MSO VENDOR
              </div>
              <h3 className="text-3xl font-black text-white mb-4">MSO as Your Vendor</h3>
              <p className="text-gray-400 leading-relaxed mb-8 text-sm">
                Best for multi-provider groups, specialty clinics, pharmacies, labs, and independent physicians who want to stay fully independent while eliminating admin burden.
              </p>

              <div className="space-y-3 mb-8">
                <div className="text-xs uppercase tracking-widest text-gray-500 mb-3">You get:</div>
                {[
                  'All 6 MSO service pillars available',
                  'Performance-based 2.5% ops fee',
                  'Optional +2.5% full RCM add-on',
                  '$250/provider/month base',
                  'Keep 100% of your practice economics',
                  'No equity required — pure vendor relationship',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm text-gray-300">
                    <i className="ri-check-line text-[#93C5FD] flex-shrink-0"></i>
                    {item}
                  </div>
                ))}
              </div>

              <div className="bg-[#1B4B7F]/20 rounded-2xl p-5 mb-8">
                <div className="text-xs text-gray-400 mb-1">Best for:</div>
                <div className="text-white font-semibold text-sm">Groups · Specialty Centers · Pharmacies · Labs · Independent Practices</div>
              </div>

              <div className="flex gap-3">
                <Link to="/services" className="flex-1 bg-[#1B4B7F] text-white text-center px-5 py-3.5 rounded-full text-sm font-bold hover:bg-[#153a63] transition-all whitespace-nowrap cursor-pointer">
                  Explore Services
                </Link>
                <Link to="/pricing" className="flex-1 border border-white/20 text-white text-center px-5 py-3.5 rounded-full text-sm font-bold hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">
                  View Pricing
                </Link>
              </div>
            </div>

            {/* Partnership Track */}
            <div className="bg-[#059669]/10 border border-[#059669]/40 rounded-3xl p-10 hover:bg-[#059669]/20 transition-all duration-300 relative">
              <div className="absolute top-6 right-6 bg-[#059669] text-white px-3 py-1 rounded-full text-xs font-bold">
                EXCLUSIVE
              </div>
              <div className="inline-flex items-center gap-2 bg-[#059669]/30 text-[#6EE7B7] px-4 py-1.5 rounded-full text-xs font-bold tracking-widest mb-6">
                <i className="ri-vip-crown-line"></i>
                TRACK 2 — PARTNERSHIP
              </div>
              <h3 className="text-3xl font-black text-white mb-4">Become a Co-Owner</h3>
              <p className="text-gray-400 leading-relaxed mb-8 text-sm">
                Designed exclusively for single-practice physicians who want to eliminate admin entirely, earn a guaranteed income, and build long-term wealth through MSO equity.
              </p>

              <div className="space-y-3 mb-8">
                <div className="text-xs uppercase tracking-widest text-gray-500 mb-3">You get:</div>
                {[
                  '$500K guaranteed annual clinical package',
                  '40% equity ownership in MedxMSO',
                  '0% ops fee — zero revenue share',
                  '0% RCM fee — fully waived',
                  'Onboarding fee waived',
                  '100% practice ownership retained',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm text-gray-300">
                    <i className="ri-check-line text-[#6EE7B7] flex-shrink-0"></i>
                    {item}
                  </div>
                ))}
              </div>

              <div className="bg-[#059669]/20 rounded-2xl p-5 mb-8">
                <div className="text-xs text-gray-400 mb-1">Best for:</div>
                <div className="text-white font-semibold text-sm">Single-Practice Physicians seeking guaranteed income + equity upside</div>
              </div>

              <div className="flex gap-3">
                <Link to="/partnership" className="flex-1 bg-[#059669] text-white text-center px-5 py-3.5 rounded-full text-sm font-bold hover:bg-[#047857] transition-all whitespace-nowrap cursor-pointer">
                  Learn More
                </Link>
                <Link to="/#contact" className="flex-1 border border-[#059669]/50 text-[#6EE7B7] text-center px-5 py-3.5 rounded-full text-sm font-bold hover:bg-[#059669]/20 transition-all whitespace-nowrap cursor-pointer">
                  Schedule a Call
                </Link>
              </div>
            </div>
          </div>

          {/* Bottom note */}
          <div className="mt-12 text-center">
            <p className="text-gray-500 text-sm">Not sure which path fits? <Link to="/pricing" className="text-[#6EE7B7] font-semibold hover:underline cursor-pointer">Compare both models side by side →</Link></p>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Confident%20successful%20doctor%20in%20modern%20bright%20medical%20office%20smiling%20with%20stethoscope%20looking%20relaxed%20and%20fulfilled%20surrounded%20by%20clean%20organized%20workspace%20with%20plants%20and%20natural%20light%20representing%20a%20thriving%20medical%20practice%20with%20professional%20management%20support%20and%20work%20life%20balance&width=1920&height=900&seq=infographic-cta-doctor&orientation=landscape"
            alt="Thriving Physician"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/95 via-[#0A1628]/80 to-transparent"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[3px] text-[#0D9488] mb-6 font-semibold">YOUR NEXT STEP</p>
            <h2 className="text-6xl lg:text-7xl font-black text-white leading-none mb-8">
              Ready to<br />
              Just Be a<br />
              <span className="text-[#0D9488]">Doctor?</span>
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed mb-10">
              Whether you want MedxMSO as your vendor or as your equity partner — the result is the same: you focus on medicine, we handle everything else.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/partnership" className="bg-[#059669] text-white px-10 py-5 rounded-full text-base font-bold hover:bg-[#047857] transition-all flex items-center gap-3 whitespace-nowrap cursor-pointer">
                Explore Partnership
                <i className="ri-vip-crown-line text-xl"></i>
              </Link>
              <Link to="/#contact" className="bg-[#0D9488] text-white px-10 py-5 rounded-full text-base font-bold hover:bg-[#0b7a70] transition-all flex items-center gap-3 whitespace-nowrap cursor-pointer shadow-2xl">
                Schedule Free Consultation
                <i className="ri-arrow-right-line text-xl"></i>
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-6">
              <Link to="/services" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer flex items-center gap-2">
                <i className="ri-arrow-right-line text-[#0D9488]"></i>
                View Services
              </Link>
              <Link to="/pricing" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer flex items-center gap-2">
                <i className="ri-arrow-right-line text-[#0D9488]"></i>
                Compare Both Models
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A1628] relative overflow-hidden">
        <div className="absolute bottom-0 left-0 right-0 text-[#1A2942] text-[180px] font-bold opacity-50 leading-none select-none pointer-events-none">
          MEDXMSO
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-4 gap-12">
            <div>
              <div className="text-white text-4xl font-bold mb-3">MedxMSO</div>
              <p className="text-gray-400 text-sm mb-16">Healthcare Management Excellence</p>
              <div className="text-white text-lg leading-relaxed">
                <p>Empowering providers</p>
                <p className="text-gray-400">since 2015</p>
              </div>
            </div>
            <div>
              <h4 className="text-white text-lg font-bold mb-6">Our Services</h4>
              <ul className="space-y-4">
                <li><Link to="/#services" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Practice Management</Link></li>
                <li><Link to="/#services" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Revenue Cycle</Link></li>
                <li><Link to="/#services" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Compliance</Link></li>
                <li><Link to="/#services" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">HR Solutions</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white text-lg font-bold mb-6">Pages</h4>
              <ul className="space-y-4">
                <li><Link to="/" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/services" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Services</Link></li>
                <li><Link to="/pricing" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Pricing</Link></li>
                <li><Link to="/partnership" className="text-[#6EE7B7] text-sm hover:text-white transition-colors cursor-pointer">Partnership</Link></li>
                <li><Link to="/why-mso" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Why MSO?</Link></li>
                <li><Link to="/#contact" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white text-lg font-bold mb-6">Connect</h4>
              <ul className="space-y-4">
                <li><a href="#" className="flex items-center gap-3 text-gray-400 text-sm hover:text-white transition-colors cursor-pointer"><i className="ri-linkedin-line text-xl"></i>LinkedIn</a></li>
                <li><a href="#" className="flex items-center gap-3 text-gray-400 text-sm hover:text-white transition-colors cursor-pointer"><i className="ri-twitter-x-line text-xl"></i>Twitter</a></li>
                <li><a href="#" className="flex items-center gap-3 text-gray-400 text-sm hover:text-white transition-colors cursor-pointer"><i className="ri-facebook-line text-xl"></i>Facebook</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-16 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">© 2024 MedxMSO. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="#" className="text-gray-500 text-sm hover:text-white transition-colors cursor-pointer">Privacy Policy</a>
              <a href="#" className="text-gray-500 text-sm hover:text-white transition-colors cursor-pointer">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
